import React from "react";
import PropTypes from "prop-types";
import {
    Typography,
    Dialog,
    DialogTitle,
    Avatar,
    DialogContent,
    DialogActions,
    Button,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { AlertTriangle } from "lucide-react";

const useStyles = makeStyles()(theme => ({
    root: {
        padding: theme.spacing(2),
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    label: {
        marginLeft: theme.spacing(1),
    },
    avatar: {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        height: 48,
        width: 48,
    },
    metalBackground: {
        backgroundColor: "var(--neutral-2)",
    },
    dialogBtn: {
        backgroundColor: "#121926",
        border: "1px solid #364152",
        color: "#CDD5DF",
        display: "flex",
        padding: "10px var(--spacing-xl, 16px)",
        justifyContent: "center",
        alignItems: "center",
        gap: "var(--spacing-sm, 6px)",
        flex: "1 0 0",
    },
}));

function ConfirmationDialog({
    title,
    content,
    cancelLabel = "Cancel",
    confirmLabel = "Confirm",
    onConfirm,
    onClose,
    open = false,
}) {
    const { classes } = useStyles();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    backgroundColor: "#0D121C",
                    borderRadius: "12px",
                    overflow: "hidden",
                },
            }}
        >
            <DialogTitle style={{ paddingTop: 24 }}>
                <Avatar
                    style={{
                        width: 48,
                        height: 48,
                        backgroundColor: "#DC6803",
                        marginBottom: "var(--spacing-xl, 16px)",
                    }}
                    variant="rounded"
                >
                    <AlertTriangle
                        style={{
                            color: "#FEDF89",
                            minWidth: "var(--space-m)",
                        }}
                        size={24}
                    />
                </Avatar>
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: 480,
                        height: 480,
                    }}
                >
                    <svg
                        width="240"
                        height="240"
                        viewBox="0 0 240 240"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="48" cy="48" r="48" stroke="#202939" />
                        <circle cx="48" cy="48" r="80" stroke="#202939" />
                        <circle cx="48" cy="48" r="112" stroke="#202939" opacity={0.6} />
                        <circle cx="48" cy="48" r="144" stroke="#202939" opacity={0.4} />
                        <circle cx="48" cy="48" r="176" stroke="#202939" opacity={0.2} />
                    </svg>
                </div>
                <Typography
                    style={{
                        fontSize: 18,
                        fontWeight: 600,
                        position: "relative",
                        zIndex: 2,
                    }}
                >
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Typography
                    variant="h6"
                    color="#9AA4B2"
                    style={{ position: "relative", zIndex: 2 }}
                >
                    {content}
                </Typography>
            </DialogContent>
            <DialogActions style={{ padding: "16px 24px" }}>
                <Button variant="outlined" onClick={onClose} className={classes.dialogBtn}>
                    {cancelLabel}
                </Button>
                <Button variant="outlined" className={classes.dialogBtn} onClick={onConfirm}>
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

ConfirmationDialog.propTypes = {
    title: PropTypes.string.isRequired,
    content: PropTypes.string,
    cancelLabel: PropTypes.string,
    confirmLabel: PropTypes.string,
    onConfirm: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool,
};

export default ConfirmationDialog;
